import React from 'react';
import { SyncNotice } from '../hooks/useSessionOrchestration';
import { CloseIcon, WarningIcon } from './ui/icons';
import './SyncBanner.css';

interface SyncBannerProps {
  notice: SyncNotice | null;
  onRetry: () => void;
  onDismiss: () => void;
}

/**
 * Non-blocking status for scans that haven't reached the server: a brass
 * "saved, will sync" note while they wait, or an oxblood error with a retry
 * when the server refused one or another step couldn't reach it. Sits above
 * the tab bar; never covers the crawl.
 */
const SyncBanner: React.FC<SyncBannerProps> = ({ notice, onRetry, onDismiss }) => {
  if (!notice) return null;

  const failed = notice.kind !== 'pending';
  const canRetry = notice.kind !== 'error' || !!notice.retry;
  return (
    <div
      className={`sync-banner ${failed ? 'sync-banner-failed' : 'sync-banner-pending'}`}
      role={failed ? 'alert' : 'status'}
      data-testid="sync-banner"
      data-kind={notice.kind}
    >
      {failed && <WarningIcon />}
      <p className="sync-banner-text">
        {failed
          ? notice.message
          : <><b>Scan saved.</b> It'll sync when you're back online.</>}
      </p>
      {canRetry && (
        <button className="sync-banner-action" onClick={onRetry} data-testid="sync-retry">
          {failed ? 'Try again' : 'Retry now'}
        </button>
      )}
      {failed && (
        <button className="sync-banner-close" onClick={onDismiss} aria-label="Dismiss">
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

export default SyncBanner;
